import type { ApiError } from '@onefootprint/request-types';

import enRequestJson from './locales/en/request.json';
import esRequestJson from './locales/es/request.json';

type Obj = Record<string, unknown>;

const uuidPattern = /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/g;

const LOGOUT_ERROR_CODES = ['E117', 'E118', 'E119'];

const isObject = (x: unknown): x is Obj => typeof x === 'object' && !!x;
const isString = (x: unknown): x is string => typeof x === 'string' && !!x;

const isApiError = (err: unknown): err is ApiError => {
  if (!err || typeof err !== 'object') {
    return false;
  }
  return 'status' in err && 'message' in err;
};

const extractStringProps = (x: unknown, acc: string[] = []): string[] => {
  const stack = [x];

  while (stack.length > 0) {
    const current = stack.pop();

    if (isString(current)) {
      acc.push(current);
    } else if (isObject(current)) {
      for (const key of Object.keys(current)) {
        stack.push(current[key]);
      }
    }
  }

  return acc;
};

const getAxiosErrorMessage = (error: unknown): string | undefined => {
  if (typeof error !== 'object' || error === null || !('response' in error)) {
    return undefined;
  }
  const { response } = error as { response?: unknown };
  if (typeof response !== 'object' || response === null || !('data' in response)) {
    return undefined;
  }
  const { data } = response as { data?: unknown };
  if (typeof data !== 'object' || data === null || !('message' in data)) {
    return undefined;
  }
  const { message } = data as { message?: unknown };
  return typeof message === 'string' ? message.trim() : undefined;
};

const getErrorMessage = (error?: unknown | Error): string => {
  if (isString(error)) {
    return error.trim();
  }
  const axiosMessage = getAxiosErrorMessage(error);
  if (axiosMessage) {
    return axiosMessage;
  }
  if (isApiError(error)) {
    return error.message?.trim() ?? 'Something went wrong';
  }
  if (typeof error === 'object' && error != null && 'message' in error && typeof error.message === 'string') {
    return error.message.trim();
  }

  return (
    extractStringProps(error)
      .filter(x => !x.match(uuidPattern))
      .join('')
      .trim() || 'Something went wrong'
  );
};

const getErrorCode = (error?: unknown | Error): string | undefined => {
  if (!error || !isApiError(error)) {
    return undefined;
  }
  return error.code ?? undefined;
};

const isLogoutError = (error: unknown) => {
  if (!isApiError(error)) {
    return undefined;
  }
  return LOGOUT_ERROR_CODES.some(code => error.code === code);
};

export { enRequestJson, esRequestJson, getErrorCode, getErrorMessage, isApiError, isLogoutError };
